import type { ParseKeys } from "i18next";
import type { LucideIcon } from "lucide-react";
import { BookOpen, Bookmark, Building2, ClipboardList, Cpu, History, LayoutDashboard, QrCode, ShieldCheck, Star, UserCircle, UserCog, UsersRound } from "lucide-react";
import { WORKSPACE_VIEWS, type WorkspaceView } from "../facilities/drilldown/nav-state.js";

/**
 * The "Dashboard & settings" workspace registry (ADR-032) — the legacy `GeneralOptions` sidebar as data: one entry per
 * `arc_view` page, in the legacy's order, with its group, its build status and the right it needs. PURE (no React), so
 * gating is unit-tested without rendering. Permission-gated entries are HIDDEN from callers who lack the right (never
 * advertise admin functions); not-yet-built entries are SHOWN and navigate to a "coming soon" page that says where the
 * capability lives today (the ADR-016 honest-defer precedent).
 */
export type WorkspaceGroup = "dashboard" | "settings";
/** What a caller needs to see an entry — the legacy's sidebar gates. */
export type WorkspaceRequirement = "none" | "facilities" | "roles" | "super" | "ratings";

export interface WorkspaceEntry {
  id: WorkspaceView;
  labelKey: ParseKeys;
  icon: LucideIcon;
  group: WorkspaceGroup;
  /** `ready` = a real page; `soon` = the honest stub. */
  status: "ready" | "soon";
  requires: WorkspaceRequirement;
  /** For a `soon` entry: where the capability lives in the app today (shown on the stub page). */
  interim?: string;
}

/** The caller's rights as the CLIENT can mirror them (the server stays authoritative on every page's data). */
export interface WorkspaceCaps {
  /** Facility-management rights (the legacy `hasFacilityManagementRights`): bypass or `manage` on any node. */
  canManageFacilities: boolean;
  /** Access-management rights (the legacy `hasAccessManagementRights`): a role with HPFP `access_manage`. */
  canManageRoles: boolean;
  isSuper: boolean;
  /** The tenant's `ratingsEnabled` flag (config-as-code, ADR-034 D6) — the Ratings page exists only with it. */
  ratingsEnabled: boolean;
}

/** Pressing the header icon lands on the Übersicht, like the legacy (ADR-033 shipped it; ADR-032 planned the flip). */
export const DEFAULT_WORKSPACE_VIEW: WorkspaceView = "overview";

/** Group-heading KEYS — this module is a pure registry and has no translator of its own. */
export const GROUP_HEADING_KEY = {
  dashboard: "workspace.dashboardHeading",
  settings: "workspace.settingsHeading",
} as const satisfies Record<WorkspaceGroup, ParseKeys>;

export const WORKSPACE_ENTRIES: readonly WorkspaceEntry[] = [
  { id: "overview", labelKey: "workspace.overview", icon: LayoutDashboard, group: "dashboard", status: "ready", requires: "none" },
  { id: "profile", labelKey: "dashboard.profile", icon: UserCircle, group: "dashboard", status: "ready", requires: "none" },
  { id: "tasks", labelKey: "dashboard.tasks", icon: ClipboardList, group: "dashboard", status: "ready", requires: "none" },
  { id: "history", labelKey: "dashboard.history", icon: History, group: "dashboard", status: "ready", requires: "none" },
  { id: "manuals", labelKey: "manuals.raw", icon: BookOpen, group: "dashboard", status: "ready", requires: "none" },
  // The bookmark the manual cards use for a favourite manual; the star stays with the ratings.
  { id: "favourites", labelKey: "workspace.favouriteManualsNav", icon: Bookmark, group: "dashboard", status: "ready", requires: "none" },
  { id: "favourite-facilities", labelKey: "dashboard.favouriteFacilities", icon: Building2, group: "dashboard", status: "ready", requires: "none" },
  { id: "ratings", labelKey: "dashboard.ratings", icon: Star, group: "dashboard", status: "ready", requires: "ratings" },
  { id: "roles", labelKey: "dashboard.roleManagement", icon: ShieldCheck, group: "settings", status: "ready", requires: "roles" },
  { id: "qr", labelKey: "workspace.printQrLabels", icon: QrCode, group: "settings", status: "ready", requires: "facilities" },
  { id: "sensors", labelKey: "dashboard.sensors", icon: Cpu, group: "settings", status: "ready", requires: "super" },
  { id: "generic-user", labelKey: "workspace.kioskProfiles", icon: UserCog, group: "settings", status: "ready", requires: "super" },
];

export function canSee(entry: WorkspaceEntry, caps: WorkspaceCaps): boolean {
  switch (entry.requires) {
    case "none":
      return true;
    case "facilities":
      return caps.isSuper || caps.canManageFacilities;
    case "roles":
      return caps.isSuper || caps.canManageRoles;
    case "super":
      return caps.isSuper;
    case "ratings":
      return caps.ratingsEnabled;
  }
}

export interface WorkspaceGroupView {
  group: WorkspaceGroup;
  headingKey: ParseKeys;
  entries: WorkspaceEntry[];
}

/** The sidebar's groups for this caller — a group with nothing visible is dropped (the legacy emitted "Einstellungen"
 *  only when the caller had facility- or access-management rights). */
export function visibleGroups(caps: WorkspaceCaps): WorkspaceGroupView[] {
  const groups: WorkspaceGroup[] = ["dashboard", "settings"];
  return groups
    .map((group) => ({ group, headingKey: GROUP_HEADING_KEY[group], entries: WORKSPACE_ENTRIES.filter((e) => e.group === group && canSee(e, caps)) }))
    .filter((g) => g.entries.length > 0);
}

export function entryFor(view: WorkspaceView): WorkspaceEntry {
  const e = WORKSPACE_ENTRIES.find((x) => x.id === view);
  if (!e) throw new Error(`no workspace entry for ${view}`); // every WORKSPACE_VIEWS value has one — asserted by the test
  return e;
}

/** Icon-only helper for the sidebar's collapsed rail + the tests: the ids in legacy order. */
export const WORKSPACE_ENTRY_IDS: readonly WorkspaceView[] = WORKSPACE_VIEWS;
export { UsersRound as WorkspaceGroupIcon };
